import useUrlState from '@ahooksjs/use-url-state';
import { Card } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateResourceModal, ResourceFilter, ResourceList } from '@/components/resource';
import { useDelayedLoading } from '@/hooks';
import { GetResourcesParams, useResourceFilterOptions, useResources } from '@/web-api/resources';

export const ResourcesPage = () => {
  const { t } = useTranslation();

  const [urlState, setUrlState] = useUrlState<{
    locations?: string[];
    genders?: string[];
    includeLanguages?: string[];
    states?: string[];
    cities?: string[];
    archived?: boolean;
  }>({
    locations: undefined,
    genders: undefined,
    includeLanguages: undefined,
    states: undefined,
    cities: undefined,
    archived: undefined,
  });

  // Normalize URL state to ensure arrays are always arrays (or undefined if empty)
  const filterParams: GetResourcesParams = useMemo(() => {
    const normalizeArray = (value: any): string[] | undefined => {
      if (!value) return undefined;
      const arr = Array.isArray(value) ? value : [value];
      return arr.length > 0 ? arr : undefined;
    };

    const normalizeBoolean = (value: any): boolean | undefined => {
      if (value === undefined || value === null) return undefined;
      if (typeof value === 'boolean') return value;
      if (value === 'true') return true;
      if (value === 'false') return false;
      return undefined;
    };

    return {
      locations: normalizeArray(urlState.locations),
      genders: normalizeArray(urlState.genders) as any,
      includeLanguages: normalizeArray(urlState.includeLanguages),
      states: normalizeArray(urlState.states),
      cities: normalizeArray(urlState.cities),
      archived: normalizeBoolean(urlState.archived),
    };
  }, [urlState]);

  const { data, isLoading } = useResources(filterParams);
  const { data: filterOptions } = useResourceFilterOptions();
  const showDelayedLoading = useDelayedLoading(isLoading);

  const onFilterChange = (key: string, value: string[] | boolean | undefined) => {
    setUrlState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Card title={t('common:resources')} extra={<CreateResourceModal />} variant='borderless'>
      <ResourceFilter
        filterOptions={filterOptions}
        filterValues={filterParams}
        onFilterChange={onFilterChange}
      />
      <ResourceList resources={data} isLoading={showDelayedLoading} />
    </Card>
  );
};
